import { useState } from "react";
import { CompanionDisconnectModal } from "@/workshop/components/canvas/companion-disconnect-modal";
import type { ModalMode } from "@/workshop/components/canvas/companion-disconnect-modal";
import { FrameErrorState } from "@/workshop/components/shapes/frame-error-state";
import { FrameWrapper } from "@/workshop/components/shapes/frame-wrapper";
import { Button } from "@/components/ui/button";

/**
 * Workshop showcase page for Z-121 error handling components.
 *
 * Demonstrates:
 * - CompanionDisconnectModal — two modes:
 *   1. ws-reconnecting (blocking modal with spinner)
 *   2. ws-failed (blocking modal with error)
 * - FrameErrorState (short + long error messages, inside a frame)
 */

// --- Mock Data ---

const SHORT_ERROR =
  "TypeError: Cannot read properties of undefined (reading 'map')";

const LONG_ERROR = `Error executing tool "web_search" in frame "Research competitors":

thread 'tool-runner' panicked at companion_core/tools/web_search.rs:89:
  called \`Result::unwrap()\` on an \`Err\` value: ConnectTimeout

Caused by:
    0: companion_core::runtime::run_tool (claude_runner.rs:245)
    1: reqwest::Client::get (client.rs:1574)
    2: Connection timed out while connecting to search API

The external service may be temporarily unavailable. Please check your network connection and try again.`;

// --- Helpers ---

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-muted">
      {children}
    </h2>
  );
}

function DemoCard({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted">
        {label}
      </span>
      <div className="rounded-lg border border-glass-stroke bg-glass-fill p-4">
        {children}
      </div>
    </div>
  );
}

// --- Page Component ---

export function ErrorHandlingPage() {
  const [modalMode, setModalMode] = useState<ModalMode | null>(null);
  const [attempt, setAttempt] = useState(3);
  const maxRetries = 10;

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="error-handling-page">
      <h1 className="text-lg font-semibold text-primary">
        Error Handling Components
      </h1>
      <p className="text-sm text-secondary">
        Z-121: UI components for companion disconnects and execution failures.
      </p>

      {/* ── Companion Disconnect Modal ─────────────────────────── */}

      <SectionTitle>Companion Disconnect Modal</SectionTitle>
      <p className="text-xs text-secondary">
        Two modes: (1) ws-reconnecting — blocking modal with spinner + attempt
        count, (2) ws-failed — blocking modal with failure message +
        troubleshooting tips.
      </p>

      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setAttempt(3);
            setModalMode("ws-reconnecting");
          }}
          data-testid="error-handling-page-show-reconnecting"
        >
          Show Reconnecting
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setAttempt(10);
            setModalMode("ws-failed");
          }}
          data-testid="error-handling-page-show-failed"
        >
          Show Failed
        </Button>
      </div>

      {modalMode && (
        <CompanionDisconnectModal
          mode={modalMode}
          reconnectAttempt={attempt}
          maxRetries={maxRetries}
          onRetry={() => {
            console.log("[workshop] Retry clicked");
            setAttempt(1);
            setModalMode("ws-reconnecting");
          }}
          onReload={() => {
            console.log("[workshop] Reload clicked");
            setModalMode(null);
          }}
        />
      )}

      {/* ── Frame Error State ──────────────────────────────────── */}

      <SectionTitle>Frame Error State — Short Message</SectionTitle>
      <p className="text-xs text-secondary">
        Inline error displayed inside a frame when execution fails. Short error
        messages render in full without truncation.
      </p>

      <DemoCard label="Code frame with error">
        <div className="w-[340px]">
          <FrameWrapper
            frameId="demo-err-1"
            frameType="code"
            title="auth-service.py"
            status="error"
          >
            <FrameErrorState
              errorMessage={SHORT_ERROR}
              onRetry={() => console.log("[workshop] Retry frame")}
              onDismiss={() => console.log("[workshop] Dismiss error")}
            />
          </FrameWrapper>
        </div>
      </DemoCard>

      <SectionTitle>Frame Error State — Long Message</SectionTitle>
      <p className="text-xs text-secondary">
        Long error messages are truncated at 200 characters with a "Show more"
        toggle to expand the full traceback.
      </p>

      <DemoCard label="Search frame with long error">
        <div className="w-[340px]">
          <FrameWrapper
            frameId="demo-err-2"
            frameType="search"
            title="Research competitors"
            status="error"
          >
            <FrameErrorState
              errorMessage={LONG_ERROR}
              onRetry={() => console.log("[workshop] Retry frame")}
              onDismiss={() => console.log("[workshop] Dismiss error")}
            />
          </FrameWrapper>
        </div>
      </DemoCard>

      <SectionTitle>Frame Error State — Workflow Frame</SectionTitle>
      <p className="text-xs text-secondary">
        Error state inside a workflow frame to verify visual consistency across
        frame types.
      </p>

      <DemoCard label="Workflow frame with error">
        <div className="w-[340px]">
          <FrameWrapper
            frameId="demo-err-3"
            frameType="workflow"
            title="Weekly Newsletter Pipeline"
            status="error"
          >
            <FrameErrorState
              errorMessage="Step 3 failed: Email template rendering error — missing variable {{subscriber.name}} in template 'weekly-digest-v2'"
              onRetry={() => console.log("[workshop] Retry frame")}
              onDismiss={() => console.log("[workshop] Dismiss error")}
            />
          </FrameWrapper>
        </div>
      </DemoCard>

      <div className="h-12" />
    </div>
  );
}
